import { z } from "zod";

/** The customer sidebar round (2026-11-01, brief `docs/briefs/customer-sidebar-round.md` rule 4 and its "After the red team's review"):
 *  a project's customer links, buttons that open the team's own tool (a shop's admin page, a CRM) for the customer of a ticket. An
 *  administrator writes each as a name and an https address with gaps (`{email}`, `{name}`, `{id}`) that are filled per customer,
 *  percent-encoded. The address before the path (scheme, host, port) is fixed, so a customer's name can never choose where a button goes;
 *  no user name or password; no brace outside the three gaps. Pure: the API, the settings card and the sidebar read this module. */

export const CUSTOMER_LINKS_MAX = 5;
export const CUSTOMER_LINK_LABEL_MAX = 30;
export const CUSTOMER_LINK_URL_MAX = 500;
/** the gaps an address may hold; `{external}` is not offered (a site's own id for a person is no WordPress user number) */
export const CUSTOMER_LINK_PLACEHOLDERS = ["email", "name", "id"] as const;
export type CustomerLinkValues = { email: string; name?: string | null; id: string };

const GAP = /\{(email|name|id)\}/g;

/** where the address's fixed part (scheme and host) ends: the first "/", "?" or "#" after "https://" */
const authorityOf = (url: string): string => url.slice("https://".length).split(/[/?#]/, 1)[0] ?? "";

/** why a template is refused, or null when it is a safe https template */
export function customerLinkProblem(url: string): string | null {
  if (!url.startsWith("https://")) return "an https:// address";
  if (url.replace(GAP, "").includes("{") || url.replace(GAP, "").includes("}")) return "only the gaps {email}, {name} and {id}";
  const authority = authorityOf(url);
  if (authority.includes("{")) return "gaps only after the host, in the path or the query";
  if (authority.includes("@")) return "no user name or password in the address";
  try {
    const u = new URL(url.replace(GAP, "x"));
    if (u.protocol !== "https:" || !u.hostname || u.username || u.password) return "an https:// address";
  } catch {
    return "an https:// address";
  }
  return null;
}

export const CustomerLinkSchema = z.object({
  label: z.string().trim().min(1).max(CUSTOMER_LINK_LABEL_MAX),
  url: z.string().trim().max(CUSTOMER_LINK_URL_MAX).superRefine((url, ctx) => {
    const problem = customerLinkProblem(url);
    if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
  }),
}).strict();
export type CustomerLink = z.infer<typeof CustomerLinkSchema>;

/** a project's list: five at most, names unique ignoring case */
export const CustomerLinksSchema = z.array(CustomerLinkSchema).max(CUSTOMER_LINKS_MAX).superRefine((links, ctx) => {
  const seen = new Set<string>();
  for (const [i, l] of links.entries()) {
    const key = l.label.toLowerCase();
    if (seen.has(key)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, "label"], message: "its own name" });
    seen.add(key);
  }
});

/** the list as a project read carries it (validated when saved; none: an empty list) */
export const CustomerLinksReadSchema = z.array(z.object({ label: z.string(), url: z.string() })).default([]);

/** the address for one customer: each gap filled with the value percent-encoded, a missing value as "" */
export function fillCustomerLink(url: string, v: CustomerLinkValues): string {
  return url.replace(GAP, (_m, key: "email" | "name" | "id") => encodeURIComponent(v[key] ?? ""));
}
